<template>
  <div class="context-menu-bar-translation-manager" v-shortcut.multiple.prevent="vm.shortcuts">
    <MenuBar>
      <template #leftItems>
        <b-dropdown toggle-class="mono" class="dropdown-default" :disabled="!canSelectTranslationProject">
          <template #button-content>
            <span>{{ currentTranslationProject?.name }}</span>
          </template>
          <b-dropdown-item
            :active="currentTranslationProject?.translationProjectId == project.translationProjectId"
            @click="switchTranslationProject(project)"
            v-for="project in sortedTranslationProjects"
            :key="project.translationProjectId"
            >{{ project.name }}
          </b-dropdown-item>
        </b-dropdown>
        <span v-tooltip="$t('translationManager.newTranslationProject')">
          <b-btn class="btn-icon mono" @click.prevent="openCreateTranslationProjectDialog">
            <Icon icon="Plus"></Icon>
          </b-btn>
        </span>
        <span v-tooltip="$t('translationManager.openTranslationProject')">
          <b-btn class="btn-icon mono" :disabled="!canSelectTranslationProject" @click.prevent="openTranslationProjectManageDialog">
            <Icon icon="Open"></Icon>
          </b-btn>
        </span>
        <span v-tooltip="$t('translationManager.deleteTranslationProject')">
          <b-btn class="btn-icon mono" :disabled="!canDelete" @click.prevent="deleteTranslationProject">
            <Icon icon="Delete"></Icon>
          </b-btn>
        </span>
        <span v-tooltip="$t('translationManager.renameTranslationProject')">
          <b-btn class="btn-icon mono" :disabled="!canRename" @click.prevent="beginRenameTranslationProject">
            <Icon icon="Rename"></Icon>
          </b-btn>
        </span>

        <ExportTranslationsButton
          id="translation-project-export"
          :translation-project-id="currentTranslationProject?.translationProjectId"
          :data-change-allowed-check="checkDataChangeAllowed"
          :translation-project-name="currentTranslationProject?.name"></ExportTranslationsButton>

        <ImportTranslationsButton
          id="translation-project-import"
          :translation-project-id="currentTranslationProject?.translationProjectId"
          :data-change-allowed-check="checkDataChangeAllowed"></ImportTranslationsButton>

        <DeepLSplitButton
          @translate-all="translate(false)"
          @translate-empty="translate(true)"
          glossary-modal-name="glossary-modal-translation-manager-menubar"
          :icon-only="true"
          :disabled="!canTranslateByAI"></DeepLSplitButton>

        <span v-tooltip="$t('translationManager.finalizeTranslationProject')">
          <b-btn class="btn-icon mono" :disabled="!canPublish" @click.prevent="publishTranslationProject">
            <Icon icon="Publish" />
          </b-btn>
        </span>
      </template>
      <template #rightItems>
        <b-dropdown toggle-class="btn-icon mono" right no-caret data-cy="context-menu">
          <template #button-content><Icon icon="Options" /></template>
          <b-dropdown-item link-class="icon-item" v-b-modal.view-options-modal>
            <div class="dd-item-container">
              <span class="dd-item-icon"><Icon icon="Preview" /></span>
              <span class="dd-item-text" data-cy="context-menu-view"> {{ $t("menubar.view") }}... </span>
            </div>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </MenuBar>
    <ViewOptionsModal></ViewOptionsModal>
    <RenameTranslationProjectModal
      v-if="currentTranslationProject"
      v-model="currentTranslationProject.name"
      v-model:show="vm.showRenameModal"
      :translation-projects="translationProjects"
      :translation-project-id="currentTranslationProject.translationProjectId"
      :title="$t('translationManager.renameDialog.title')"
      :ok-button-text="$t('OK')"
      :max-characters-name="128"
      @confirm="confirmRename"
      @cancel="cancelRename" />
    <CreateTranslationProjectModal
      :translation-projects-names="translationProjectNames"
      v-model:show="vm.showCreateModal"
      @confirm="confirmCreateTranslationProject"></CreateTranslationProjectModal>
    <OpenTranslationProjectModal
      :translation-projects="translationProjects"
      v-model:show="vm.showOpenModal"
      @confirm="switchTranslationProject"></OpenTranslationProjectModal>
    <ProgressModal :progress="vm.progressHandler.progress" @cancel="publishCancelled" :min-open-time="1000" :auto-close="true"></ProgressModal>
  </div>
</template>

<script setup lang="ts">
import DeepLSplitButton from "@common/components/DeepLSplitButton.vue";
import Icon from "@common/components/Icon.vue";
import MenuBar from "@common/components/MenuBar/MenuBar.vue";
import ProgressModal from "@common/components/ProgressModal.vue";
import { NavMenuItem, ShortcutDefinition } from "@medapro-types/common/common";
import { ProgressHandler } from "@medapro-types/common/components/ProgressHandling";
import { CreateTranslationProjectData, TranslationProject } from "@medapro-types/TranslationManager/translationManager";
import translationManagerApi from "@services/translationManager";
import CreateTranslationProjectModal from "@TranslationManager/components/CreateTranslationProjectModal.vue";
import RenameTranslationProjectModal from "@TranslationManager/components/RenameTranslationProjectModal.vue";
import {
  useTranslationManagerActions,
  useTranslationManagerGetters,
  useTranslationManagerState
} from "@TranslationManager/composables/useTranslationManagerStore";
import { naturalSort } from "@util/naturalSort";
import emitter from "tiny-emitter/instance";
import { computed, DeepReadonly, onMounted, onUnmounted, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import ExportTranslationsButton from "./ExportButton.vue";
import ImportTranslationsButton from "./ImportButton.vue";
import OpenTranslationProjectModal from "./OpenTranslationProjectModal.vue";
import ViewOptionsModal from "./OptionsMenu/ViewOptionsModal.vue";

//#region General
const { t: $t } = useI18n();

const router = useRouter();
//#endregion

//#region Store
const { translationProjects, currentTranslationProject } = useTranslationManagerState();
const {
  renameTranslationProject,
  deleteTranslationProject: deleteProject,
  createTranslationProject,
  translateEntriesWithDeepL,
  publishTranslationProject: publishProject
} = useTranslationManagerActions();
const { hasUnsavedData } = useTranslationManagerGetters();
//#endregion

//#region ViewModel
type ViewModel = {
  showRenameModal: boolean;
  showCreateModal: boolean;
  showOpenModal: boolean;
  exportMenuItems: Array<NavMenuItem>;
  progressHandler: ProgressHandler;
  shortcuts: ShortcutDefinition[];
};

const vm = reactive<ViewModel>({
  showRenameModal: false,
  showCreateModal: false,
  showOpenModal: false,
  exportMenuItems: [
    {
      text: $t("translationManager.exportExcel"),
      icon: "mdp-FileExcel"
    },
    {
      text: $t("translationManager.importXliff"),
      icon: "mdp-Export"
    }
  ],
  progressHandler: ProgressHandler.createIndeterminate({ id: "translation-project-progress-modal", isCancellable: true }),
  shortcuts: [
    {
      key: "n",
      ctrlKey: true,
      altKey: true,
      callback: () => openCreateTranslationProjectDialog()
    },
    {
      key: "o",
      ctrlKey: true,
      altKey: true,
      callback: () => openTranslationProjectManageDialog()
    },
    {
      ctrlKey: true,
      key: "delete",
      callback: () => deleteTranslationProject()
    },
    {
      key: "F2",
      callback: () => beginRenameTranslationProject()
    }
  ]
});
//#endregion

//#region Saving
const checkDataChangeAllowed = () => {
  return !hasUnsavedData.value || confirm($t("confirmNavigation"));
};
//#endregion

//#region Project selection
const translationProjectNames = computed(() => {
  return translationProjects.value.map((project) => project.name);
});

const canSelectTranslationProject = computed(() => {
  return translationProjects.value.length > 0;
});

const sortedTranslationProjects = computed(() => {
  const sorted = naturalSort(translationProjects.value, "name");
  return sorted;
});

const switchTranslationProject = async (project: DeepReadonly<TranslationProject>) => {
  if (project.translationProjectId === currentTranslationProject.value?.translationProjectId) {
    return;
  }

  router.push({
    name: "translation-manager",
    params: {
      translationProjectId: project.translationProjectId
    }
  });
};

const openTranslationProjectManageDialog = () => {
  if (!vm.showOpenModal) {
    vm.showOpenModal = true;
  }
};
//#endregion

//#region Project creation
const openCreateTranslationProjectDialog = () => {
  if (!vm.showCreateModal) {
    vm.showCreateModal = true;
  }
};

const confirmCreateTranslationProject = async (creationData: CreateTranslationProjectData) => {
  if (!checkDataChangeAllowed()) {
    return;
  }

  const createdProject = await createTranslationProject(creationData);
  if (createdProject) {
    await switchTranslationProject(createdProject);
  }
};
//#endregion

//#region Project rename
const canRename = computed(() => {
  return currentTranslationProject.value != null;
});

const beginRenameTranslationProject = () => {
  if (!vm.showRenameModal && currentTranslationProject.value) {
    vm.showRenameModal = true;
  }
};

const confirmRename = async (newName: string) => {
  await renameTranslationProject({ translationProjectId: currentTranslationProject.value?.translationProjectId, name: newName });
};

const cancelRename = () => {};
//#endregion

//#region Project delete
const canDelete = computed(() => {
  return currentTranslationProject.value != null;
});

const deleteTranslationProject = async () => {
  if (!currentTranslationProject.value) {
    return;
  }
  if (!confirm($t("translationManager.deleteTranslationProjectQuestion", { projectName: currentTranslationProject.value.name }))) {
    return;
  }

  await deleteProject(currentTranslationProject.value);
};
//#endregion

//#region Publish
const canPublish = computed(() => {
  return currentTranslationProject.value != null;
});

const publishTranslationProject = async () => {
  if (!currentTranslationProject.value || !checkDataChangeAllowed()) {
    return;
  }

  const hasOverlongValues = await translationManagerApi.hasOverlongTargetValues(currentTranslationProject.value.translationProjectId);
  if (
    (hasOverlongValues && !confirm($t("translationManager.askPublishProjectWithOverlongValues"))) ||
    (!hasOverlongValues && !confirm($t("translationManager.askPublishProject")))
  ) {
    return;
  }

  await publishProject({ progressHandler: vm.progressHandler });
};

const publishCancelled = () => {
  vm.progressHandler.cancel(`${$t("translationManager.cancellingPublish")}...`);
};
//#endregion

//#region DeepL translation
const canTranslateByAI = computed(() => {
  return currentTranslationProject.value != null;
});

const translate = async (onlyEmpty: boolean) => {
  if (!checkDataChangeAllowed()) {
    return;
  }

  await translateEntriesWithDeepL({
    translationProjectId: currentTranslationProject.value?.translationProjectId,
    onlyEmpty: onlyEmpty
  });
};

//#endregion

//#region Lifecycle
onMounted(() => {
  emitter.on("create-TM-project", openCreateTranslationProjectDialog);
  emitter.on("delete-TM-project", deleteTranslationProject);
  emitter.on("open-TM-project", openTranslationProjectManageDialog);
  emitter.on("rename-TM-project", beginRenameTranslationProject);
});

onUnmounted(() => {
  emitter.off("create-TM-project", openCreateTranslationProjectDialog);
  emitter.off("delete-TM-project", deleteTranslationProject);
  emitter.off("open-TM-project", openTranslationProjectManageDialog);
  emitter.off("rename-TM-project", beginRenameTranslationProject);
});
//#endregion
</script>
